import { useState, useEffect } from 'react'
import { formatVersion } from '../utils/version'
import { useParams } from 'react-router-dom'
import { Search, Ban, CheckCircle, XCircle, MoreVertical } from 'lucide-react'

interface Device {
  device_id: string
  app_id: string
  current_version: number
  status: 'active' | 'banned' | 'deactivated'
  last_seen: string
  upgrade_count: number
}

/**
 * 将十进制版本号转换为十六进制格式
 * 例：65536 → 0x00010000
 * 例：66051 → 0x00010203
 */

export default function Devices() {
  const { appId } = useParams<{ appId: string }>()
  const [devices, setDevices] = useState<Device[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null)
  const [showBanModal, setShowBanModal] = useState(false)
  const [banReason, setBanReason] = useState('')

  useEffect(() => {
    loadDevices()
  }, [appId])

  const loadDevices = async () => {
    try {
      const response = await fetch(`/api/admin/apps/${appId}/devices`)
      const data = await response.json()
      setDevices(data.devices || [])
    } catch (error) {
      console.error('加载设备失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleBan = async () => {
    if (!selectedDevice || !banReason) return

    try {
      const response = await fetch(`/api/admin/devices/${selectedDevice.device_id}/ban`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: banReason, duration: 'permanent' })
      })

      if (response.ok) {
        await loadDevices()
        setShowBanModal(false)
        setBanReason('')
        setSelectedDevice(null)
      }
    } catch (error) {
      console.error('封禁设备失败:', error)
    }
  }

  const handleUnban = async (deviceId: string) => {
    try {
      const response = await fetch(`/api/admin/devices/${deviceId}/unban`, {
        method: 'POST'
      })

      if (response.ok) {
        await loadDevices()
      }
    } catch (error) {
      console.error('解封设备失败:', error)
    }
  }

  const filteredDevices = devices.filter(device => {
    const matchesSearch = device.device_id.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || device.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center gap-1 text-green-400"><CheckCircle size={14} /> 正常</span>
      case 'banned':
        return <span className="inline-flex items-center gap-1 text-red-400"><XCircle size={14} /> 封禁</span>
      default:
        return <span className="text-gray-400">停用</span>
    }
  }

  if (loading) {
    return <div className="text-center py-12 text-gray-400">加载中...</div>
  }

  return (
    <div className="space-y-6">
      {/* 搜索和筛选 */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10 pointer-events-none" size={18} />
          <input
            type="text"
            placeholder="搜索设备 ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ paddingLeft: '48px' }}
            className="input w-full"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input"
        >
          <option value="all">全部状态</option>
          <option value="active">正常</option>
          <option value="banned">封禁</option>
          <option value="deactivated">停用</option>
        </select>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card">
          <p className="text-sm text-gray-400">总设备数</p>
          <p className="text-2xl font-bold text-white mt-1">{devices.length}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-400">正常设备</p>
          <p className="text-2xl font-bold text-green-400 mt-1">
            {devices.filter(d => d.status === 'active').length}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-400">封禁设备</p>
          <p className="text-2xl font-bold text-red-400 mt-1">
            {devices.filter(d => d.status === 'banned').length}
          </p>
        </div>
      </div>

      {/* 设备列表 */}
      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">设备列表</h3>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>设备 ID</th>
                <th>当前版本</th>
                <th>状态</th>
                <th>最后在线</th>
                <th>升级次数</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredDevices.map((device) => (
                <tr key={device.device_id}>
                  <td className="font-mono text-sm">{device.device_id}</td>
                  <td className="font-mono text-sm">{formatVersion(device.current_version)}</td>
                  <td>{getStatusBadge(device.status)}</td>
                  <td>{new Date(device.last_seen).toLocaleString('zh-CN')}</td>
                  <td>{device.upgrade_count}</td>
                  <td>
                    <div className="flex gap-2">
                      {device.status === 'active' ? (
                        <button
                          onClick={() => {
                            setSelectedDevice(device)
                            setShowBanModal(true)
                          }}
                          className="text-red-400 hover:text-red-300"
                          title="封禁设备"
                        >
                          <Ban size={16} />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleUnban(device.device_id)}
                          className="text-green-400 hover:text-green-300"
                          title="解封设备"
                        >
                          <CheckCircle size={16} />
                        </button>
                      )}
                      <button className="text-gray-400 hover:text-white">
                        <MoreVertical size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 封禁弹窗 */}
      {showBanModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card w-96">
            <h3 className="text-lg font-semibold text-white mb-4">封禁设备</h3>
            <p className="text-gray-400 mb-4">设备：{selectedDevice?.device_id}</p>
            <textarea
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              placeholder="请输入封禁原因..."
              className="input w-full h-32 mb-4"
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowBanModal(false)
                  setBanReason('')
                  setSelectedDevice(null)
                }}
                className="btn-secondary"
              >
                取消
              </button>
              <button onClick={handleBan} className="btn-primary">
                确认封禁
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
